import React, { useState, useEffect } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Volume2,
  Eye,
  Moon,
  Sun,
  User,
  
  Palette,
  Mic,
  AlertTriangle,
  Siren,
  Bell,
  TrainFront,
  Copy,
  Check,
  Shield,
  Move,
  QrCode,
  Scan,
  Magnet,
  Timer,
} from "lucide-react";
import { HelpAlertType } from "@/lib/userSettings";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { getUserSettings, saveUserSettings, saveUserSettingsAsync } from "@/lib/userSettings";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
// VoiceCloningModal and TwoFactorSetup replaced with navigation
import { SmartHomeSettingsSection } from "@/components/settings/SmartHomeSettings";
import { NightMonitoringSettings } from "@/components/settings/NightMonitoringSettings";

import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useFeature } from "@/hooks/useFeature";
import { FeatureGuard } from "@/components/FeatureGuard";

interface SettingsSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function SettingsSection({ title, icon, children }: SettingsSectionProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl border border-border p-6 space-y-4"
    >
      <div className="flex items-center gap-3 pb-3 border-b border-border">
        <div className="p-2 rounded-xl bg-primary/10 text-primary">{icon}</div>
        <h2 className="text-lg font-bold">{title}</h2>
      </div>
      {children}
    </motion.div>
  );
}

interface SettingsRowProps {
  label: string;
  description?: string;
  children: React.ReactNode;
}

function SettingsRow({ label, description, children }: SettingsRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <div className="min-w-0 flex-1">
        <p className="font-medium">{label}</p>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

/**
 * Paint a DOM-level full-screen "changing language" overlay immediately.
 * Mirrors the pre-React loader markup in index.html so the overlay can
 * persist seamlessly across the page reload (no white flash).
 */
function paintLangChangeOverlay(message: string, dir: "rtl" | "ltr") {
  if (typeof document === "undefined") return;
  if (document.getElementById("ayuni-lang-loader")) return;

  const styleId = "ayuni-lang-loader-style";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent =
      "@keyframes ayuni-lang-spin { to { transform: rotate(360deg); } }" +
      "#ayuni-lang-loader { position: fixed; inset: 0; z-index: 2147483647;" +
      " display: flex; flex-direction: column; align-items: center;" +
      " justify-content: center; gap: 16px; background: #0b0b10;" +
      " color: #f5f5f7; font-family: Rubik, Poppins, system-ui, sans-serif;" +
      " font-size: 18px; font-weight: 500; }" +
      "#ayuni-lang-loader .spinner { width: 48px; height: 48px;" +
      " border-radius: 50%; border: 4px solid rgba(255,255,255,0.15);" +
      " border-top-color: #1e8e96; animation: ayuni-lang-spin 0.9s linear infinite; }";
    document.head.appendChild(style);
  }

  const overlay = document.createElement("div");
  overlay.id = "ayuni-lang-loader";
  overlay.setAttribute("role", "status");
  overlay.setAttribute("aria-live", "polite");
  overlay.setAttribute("dir", dir);

  const spinner = document.createElement("div");
  spinner.className = "spinner";

  const label = document.createElement("p");
  label.style.margin = "0";
  label.textContent = message;

  overlay.appendChild(spinner);
  overlay.appendChild(label);
  document.body.insertBefore(overlay, document.body.firstChild);
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { t, dir, isRTL } = useCaregiverTranslation();
  const isVoiceCloningEnabled = useFeature("voiceCloning");
  const [settings, setSettings] = useState(getUserSettings());
  const [isDark, setIsDark] = useState(settings.theme === "dark");
  const [isMuted, setIsMuted] = useState(settings.isMuted);
  const [speechRate, setSpeechRate] = useState([settings.speechRate]);
  const [fontSize, setFontSize] = useState(settings.fontSize);
  const [voiceGender, setVoiceGender] = useState(settings.voiceGender);
  const [helpAlertType, setHelpAlertType] = useState(settings.helpAlertType || "voice");
  const [useClonedVoice, setUseClonedVoice] = useState(settings.useClonedVoice || false);
  const [clonedVoiceId, setClonedVoiceId] = useState(settings.clonedVoiceId || null);
  const [speechLanguage, setSpeechLanguage] = useState(settings.speechLanguage || settings.language);
  const canUseClonedVoice = isVoiceCloningEnabled && !!clonedVoiceId;
  
  // Auth
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  
  // Design admin panel - dispatches global event
  
  // Profile editing
  const [userName, setUserName] = useState(settings.name);
  const [userGender, setUserGender] = useState(settings.gender);
  const [userLanguage, setUserLanguage] = useState(settings.language);
  
  // Head tracking sensitivity (amplification)
  const [headTrackingSensitivity, setHeadTrackingSensitivity] = useState([settings.headTrackingSensitivity]);
  // Gaze tracking smoothing
  const [gazeTrackingSmoothing, setGazeTrackingSmoothing] = useState([settings.gazeTrackingSmoothing]);
  
  // New SRS settings
  const [dwellTime, setDwellTime] = useState([settings.dwellTime]);
  const [firstItemDelay, setFirstItemDelay] = useState(settings.firstItemDelay);
  const [cursorMagnetism, setCursorMagnetism] = useState(settings.cursorMagnetism);
  const [cursorSmoothing, setCursorSmoothing] = useState(settings.cursorSmoothing);

  // Sync voice gender with user gender on initial load if they don't match
  useEffect(() => {
    if ((userGender === "male" || userGender === "female") && voiceGender !== userGender) {
      setVoiceGender(userGender);
      saveUserSettings({ voiceGender: userGender });
    }
  }, []); // Run only on mount




  // Track previous language to detect changes
  const [prevLanguage, setPrevLanguage] = React.useState(userLanguage);

  // Save settings when they change
  useEffect(() => {
    const payload = {
      theme: (isDark ? "dark" : "light") as "light" | "dark",
      isMuted,
      speechRate: speechRate[0],
      fontSize,
      voiceGender,
      helpAlertType,
      useClonedVoice,
      clonedVoiceId,
      name: userName,
      gender: userGender,
      language: userLanguage,
      speechLanguage: speechLanguage === userLanguage ? null : speechLanguage,
      headTrackingSensitivity: headTrackingSensitivity[0],
      gazeTrackingSmoothing: gazeTrackingSmoothing[0],
      dwellTime: dwellTime[0],
      firstItemDelay,
      cursorMagnetism,
      cursorSmoothing,
    };

    const languageChanged = userLanguage !== prevLanguage;

    if (languageChanged) {
      // Wait for the cloud write to land before reloading, otherwise the
      // post-reload sync (useAuth) can read a stale row and revert the
      // language back. Fall back to reload after a short timeout so the user
      // is never stuck if the network is slow / offline; the local-write
      // grace window in useAuth will still protect against stale overwrites.
      setPrevLanguage(userLanguage);

      // Show a DOM-level overlay immediately AND persist a flag so the
      // next page paints the same overlay before React mounts — this
      // eliminates the white flash between unload and the new document.
      const message = t("changingLanguage");
      const newDir = (userLanguage === "he" || userLanguage === "ar") ? "rtl" : "ltr";
      try {
        sessionStorage.setItem(
          "ayuni_changing_language",
          JSON.stringify({ message, dir: newDir })
        );
      } catch {}
      // Also paint it now (covers the time before reload() is called).
      paintLangChangeOverlay(message, newDir);

      const timeout = new Promise<void>((resolve) => setTimeout(resolve, 2000));
      Promise.race([saveUserSettingsAsync(payload).then(() => undefined), timeout])
        .finally(() => {
          window.location.reload();
        });
      return;
    }

    const newSettings = saveUserSettings(payload);
    setSettings(newSettings);
  }, [isDark, isMuted, speechRate, fontSize, voiceGender, helpAlertType, useClonedVoice, clonedVoiceId, userName, userGender, userLanguage, prevLanguage, headTrackingSensitivity, gazeTrackingSmoothing, dwellTime, firstItemDelay, cursorMagnetism, cursorSmoothing, speechLanguage]);

  // Refresh settings when returning from voice cloning page
  useEffect(() => {
    const refreshedSettings = getUserSettings();
    if (refreshedSettings.clonedVoiceId !== clonedVoiceId) {
      setClonedVoiceId(refreshedSettings.clonedVoiceId || null);
      if (refreshedSettings.clonedVoiceId) {
        setUseClonedVoice(true);
      }
    }
  }, []);

  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  // Font size is applied per-route in MainLayout (user-facing screens only)
  // Trigger a re-render of MainLayout by saving settings (which updates localStorage)
  React.useEffect(() => {
    // Settings page uses MainLayout which reads fontSize from userSettings
    // No global HTML class needed - MainLayout handles scoping
  }, [fontSize]);

  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  return (
    <MainLayout>
      {/* The "changing language" overlay is painted at the DOM level (see
          paintLangChangeOverlay + index.html pre-React loader) so it persists
          across the page reload without a white flash. */}
      <div className="flex-1 flex flex-col min-h-0" dir={dir}>
        {/* Header */}
        <header className="flex-shrink-0 flex items-center gap-3 px-6 py-4 border-b border-border bg-card/50">
          <h1 className="text-2xl font-bold">{t("settingsTitle")}</h1>
        </header>

        {/* Settings Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          <div className="space-y-4 max-w-2xl">
          {/* Profile - Row 0 */}
          <SettingsSection title={userGender === "female" ? t("profileFemale") : t("profileMale")} icon={<User className="w-5 h-5" />}>
            <div className="space-y-4 rounded-xl p-2">
              <SettingsRow label={t("name")}>
                <Input
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder={t("enterName")}
                  className="w-40"
                  dir={dir}
                />
              </SettingsRow>

              <SettingsRow label={t("addressing")}>
                <div className="flex gap-2">
                  {(["female", "male", "neutral"] as const).map((g) => (
                    <Button
                      key={g}
                      variant={userGender === g ? "default" : "outline"}
                      size="sm"
                      data-scan-item
                      onClick={() => {
                        setUserGender(g);
                        // Sync voice gender with user gender (if not using cloned voice)
                        if (g === "female" || g === "male") {
                          setVoiceGender(g);
                        }
                      }}
                    >
                      {g === "female" ? t("female") : g === "male" ? t("male") : t("neutral")}
                    </Button>
                  ))}
                </div>
              </SettingsRow>

              <SettingsRow label={t("language")}>
                <div className="flex gap-2 flex-wrap">
                  {(["he", "en", "ar", "ru"] as const).map((lang) => (
                    <Button
                      key={lang}
                      variant={userLanguage === lang ? "default" : "outline"}
                      size="sm"
                      data-scan-item
                      onClick={() => { setUserLanguage(lang); setSpeechLanguage(lang); }}
                    >
                      {lang === "he" ? t("hebrew") : lang === "en" ? t("english") : lang === "ar" ? t("arabic") : t("russian")}
                    </Button>
                  ))}
                </div>
              </SettingsRow>
            </div>
          </SettingsSection>

          {/* Appearance - Row 1 */}
          <SettingsSection title={t("appearance")} icon={<Palette className="w-5 h-5" />}>
            <div className="space-y-4 rounded-xl p-2">
              <SettingsRow label={isDark ? t("darkMode") : t("lightMode")} description={t("darkModeDesc")}>
                <div className="flex items-center gap-3" dir="ltr">
                  <Sun
                    className={cn(
                      "w-5 h-5 transition-colors",
                      isDark ? "text-muted-foreground" : "text-primary",
                    )}
                  />
                  <Switch
                    checked={isDark}
                    onCheckedChange={setIsDark}
                    aria-label={t("darkMode")}
                    data-scan-item
                  />
                  <Moon
                    className={cn(
                      "w-5 h-5 transition-colors",
                      isDark ? "text-primary" : "text-muted-foreground",
                    )}
                  />
                </div>
              </SettingsRow>

              <SettingsRow label={t("fontSize")}>
                <div className="flex gap-3 items-end">
                  {(["small", "medium", "large"] as const).map((size) => (
                    <Button
                      key={size}
                      variant={fontSize === size ? "default" : "outline"}
                      onClick={() => setFontSize(size)}
                      data-scan-item
                      className={cn(
                        "transition-all min-w-16 h-12 px-4",
                        size === "small" && "text-xs",
                        size === "medium" && "text-base",
                        size === "large" && "text-xl"
                      )}
                    >
                      {size === "small" ? t("small") : size === "medium" ? t("medium") : t("large")}
                    </Button>
                  ))}
                </div>
              </SettingsRow>
            </div>
          </SettingsSection>

          {/* Sound - Row 2 */}
          <SettingsSection title={t("soundAndVoice")} icon={<Volume2 className="w-5 h-5" />}>
            <div className="space-y-4 rounded-xl p-2">
              <SettingsRow label={t("voiceSelection")} description={t("voiceSelectionDesc")}>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant={!useClonedVoice && voiceGender === "female" ? "default" : "outline"}
                    size="sm"
                    data-scan-item
                    onClick={() => {
                      setUseClonedVoice(false);
                      setVoiceGender("female");
                    }}
                    className="flex items-center gap-1"
                  >
                    <Mic className="w-3 h-3" />
                    {t("female")}
                  </Button>
                  <Button
                    variant={!useClonedVoice && voiceGender === "male" ? "default" : "outline"}
                    size="sm"
                    data-scan-item
                    onClick={() => {
                      setUseClonedVoice(false);
                      setVoiceGender("male");
                    }}
                    className="flex items-center gap-1"
                  >
                    <Mic className="w-3 h-3" />
                    {t("male")}
                  </Button>
                  {canUseClonedVoice && (
                    <Button
                      variant={useClonedVoice ? "default" : "outline"}
                      size="sm"
                      data-scan-item
                      onClick={() => setUseClonedVoice(true)}
                      className="flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" />
                      {t("myVoice")}
                    </Button>
                  )}
                </div>
              </SettingsRow>

              <SettingsRow label={t("speechLanguage")} description={t("speechLanguageDesc")}>
                <div className="flex gap-2 flex-nowrap">
                  {([
                    { lang: "he", label: "עברית" },
                    { lang: "en", label: "English" },
                    { lang: "ar", label: "العربية" },
                    { lang: "ru", label: "Русский" },
                  ] as const).map((opt) => (
                    <Button
                      key={opt.lang}
                      variant={speechLanguage === opt.lang ? "default" : "outline"}
                      size="sm"
                      data-scan-item
                      onClick={() => setSpeechLanguage(opt.lang)}
                      className="flex items-center gap-1 px-4 whitespace-nowrap"
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </SettingsRow>

              {isVoiceCloningEnabled && (
                <SettingsRow label={t("voiceCloning")} description={t("voiceCloningDesc")}>
                  <Button
                    variant="outline"
                    size="sm"
                    data-scan-item
                    onClick={() => navigate("/settings/voice/cloning")}
                    className="flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    {clonedVoiceId ? t("cloneAgain") : t("cloneVoice")}
                  </Button>
                </SettingsRow>
              )}

              <SettingsRow label={t("speechSpeed")}>
                <div className="w-32">
                  <Slider
                    value={speechRate}
                    onValueChange={setSpeechRate}
                    min={0.5}
                    max={1.5}
                    step={0.1}
                    className="w-full"
                  />
                </div>
              </SettingsRow>

              <SettingsRow label={t("helpAlertSound")} description={t("helpAlertSoundDesc")}>
                <div className="flex gap-2 flex-nowrap">
                  {([
                    { type: "voice" as HelpAlertType, labelKey: "voiceAlert", icon: <Mic className="w-4 h-4" /> },
                    { type: "siren" as HelpAlertType, labelKey: "sirenAlert", icon: <Siren className="w-4 h-4" /> },
                    { type: "beep" as HelpAlertType, labelKey: "beepAlert", icon: <Bell className="w-4 h-4" /> },
                    { type: "train" as HelpAlertType, labelKey: "trainAlert", icon: <TrainFront className="w-4 h-4" /> },
                  ]).map((opt) => (
                    <Button
                      key={opt.type}
                      variant={helpAlertType === opt.type ? "default" : "outline"}
                      size="sm"
                      data-scan-item
                      onClick={() => setHelpAlertType(opt.type)}
                      className="flex items-center gap-1 px-4 whitespace-nowrap"
                    >
                      {opt.icon}
                      {t(opt.labelKey as any)}
                    </Button>
                  ))}
                </div>
              </SettingsRow>
            </div>
          </SettingsSection>




          {/* Accessibility - Row 3 (only show if head_tracking or gaze_tracking active) */}
          {(settings.controlMethod === "head_tracking" || settings.controlMethod === "gaze_tracking") && (
          <SettingsSection title={t("accessibility")} icon={<Eye className="w-5 h-5" />}>
            <div className="space-y-4 rounded-xl p-2">
              {/* Head Tracking - Movement Range */}
              {settings.controlMethod === "head_tracking" && (
                <SettingsRow
                  label={t("headTrackingRange")}
                  description={t("headTrackingRangeDesc")}
                >
                  <div className="flex flex-col gap-2 w-64">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{t("rangeWide")}</span>
                      <Slider
                        value={headTrackingSensitivity}
                        onValueChange={setHeadTrackingSensitivity}
                        min={1}
                        max={10}
                        step={1}
                        className="flex-1"
                      />
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{t("rangeNarrow")}</span>
                    </div>
                  </div>
                </SettingsRow>
              )}
              
              {/* Gaze Tracking - Cursor Smoothing */}
              {settings.controlMethod === "gaze_tracking" && (
                <SettingsRow
                  label={t("gazeTrackingSmoothing")}
                  description={t("gazeTrackingSmoothingDesc")}
                >
                  <div className="flex flex-col gap-2 w-64">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{t("smoothingLow")}</span>
                      <Slider
                        value={gazeTrackingSmoothing}
                        onValueChange={setGazeTrackingSmoothing}
                        min={1}
                        max={10}
                        step={1}
                        className="flex-1"
                      />
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{t("smoothingHigh")}</span>
                    </div>
                  </div>
                </SettingsRow>
              )}
            </div>
          </SettingsSection>
          )}

          {/* Scanning Settings section removed — "First Item Delay" feature disabled for all users */}

          {/* Cursor Settings - Show for head/gaze tracking */}
          {(settings.controlMethod === "head_tracking" || settings.controlMethod === "gaze_tracking") && (
            <SettingsSection title={t("cursorSettings")} icon={<Magnet className="w-5 h-5" />}>
              {/* Dwell Time */}
              <SettingsRow
                label={t("dwellTime")}
                description={t("dwellTimeDesc")}
              >
                <div className="flex flex-col gap-2 w-64">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{t("fast")}</span>
                    <Slider
                      value={dwellTime}
                      onValueChange={setDwellTime}
                      min={0.5}
                      max={3.0}
                      step={0.25}
                      className="flex-1"
                    />
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{t("slow")}</span>
                  </div>
                  <span className="text-xs text-center text-muted-foreground">
                    {dwellTime[0].toFixed(2)} {t("seconds")}
                  </span>
                </div>
              </SettingsRow>

              {/* Cursor Magnetism */}
              <SettingsRow
                label={t("cursorMagnetism")}
                description={t("cursorMagnetismDesc")}
              >
                <div className="flex gap-2">
                  {(["off", "weak", "strong"] as const).map((mag) => (
                    <Button
                      key={mag}
                      variant={cursorMagnetism === mag ? "default" : "outline"}
                      size="sm"
                      data-scan-item
                      onClick={() => setCursorMagnetism(mag)}
                    >
                      {mag === "off" ? t("offSetting") : mag === "weak" ? t("weakSetting") : t("strongSetting")}
                    </Button>
                  ))}
                </div>
              </SettingsRow>

              {/* Cursor Smoothing */}
              <SettingsRow
                label={t("cursorSmoothingSetting")}
                description={t("cursorSmoothingDesc")}
              >
                <div className="flex gap-2">
                  {(["low", "medium", "high"] as const).map((smooth) => (
                    <Button
                      key={smooth}
                      variant={cursorSmoothing === smooth ? "default" : "outline"}
                      size="sm"
                      data-scan-item
                      onClick={() => setCursorSmoothing(smooth)}
                    >
                      {smooth === "low" ? t("low") : smooth === "medium" ? t("medium") : t("high")}
                    </Button>
                  ))}
                </div>
              </SettingsRow>
            </SettingsSection>
          )}

          {/* Security - Only show if user is logged in */}
          {user && (
            <SettingsSection title={t("security")} icon={<Shield className="w-5 h-5" />}>
              <SettingsRow 
                label={t("twoFactorSetup")} 
                description={t("twoFactorSetupDesc")}
              >
                <Button
                  variant="outline"
                  size="sm"
                  data-scan-item
                  onClick={() => navigate("/settings/2fa")}
                  className="flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  {t("setup2FA")}
                </Button>
              </SettingsRow>
            </SettingsSection>
          )}


          {/* Design Control Center - Admin only */}
          {isAdmin && (
            <SettingsSection title="עיצוב מתקדם" icon={<Palette className="w-5 h-5" />}>
              <SettingsRow 
                label="מרכז שליטה בעיצוב" 
                description="שנה צבעים, רקעים ומסגרות לכל מסך"
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.dispatchEvent(new Event("open-design-panel"))}
                  className="flex items-center gap-2"
                >
                  <Palette className="w-4 h-4" />
                  פתח פאנל
                </Button>
              </SettingsRow>
          </SettingsSection>
          )}

          {/* Smart Home */}
          <FeatureGuard name="smartHome">
            <SmartHomeSettingsSection />
          </FeatureGuard>
          
          {/* Night Monitoring (EyeControl only) */}
          <FeatureGuard name="nightMonitoring">
            <SettingsSection title={(t as any)("nightMonitoringTitle") || "ניטור לילה"} icon={<Moon className="w-5 h-5" />}>
              <NightMonitoringSettings />
            </SettingsSection>
          </FeatureGuard>

          </div>
        </div>
      </div>
    </MainLayout>
  );
}
